import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'

import type { Job } from '../../hooks/useFleet'
import type { User } from '../../hooks/useUsers'

interface Props {
  jobs: Job[]
  users: User[]
  renderTrailing?: (job: Job) => ReactNode
  onDragStart?: (jobId: string) => void
  onDragEnd?: () => void
}

/// Wave 1 E1.2 — Job queue panel. Renders a scrollable list of jobs and
/// makes each card draggable via HTML5 drag and drop. The DriversPanel
/// hosts drop targets and triggers the assign mutation.
export function JobQueue({ jobs, users, renderTrailing, onDragStart, onDragEnd }: Props) {
  const { t } = useTranslation()
  const userById = new Map(users.map((u) => [u.id, u]))

  return (
    <div className="bg-white rounded-lg border border-slate-200 flex flex-col min-h-0 overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">
          {t('dispatch.jobQueue', 'Job Queue')} <span className="text-slate-400 ml-1">({jobs.length})</span>
        </h2>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
        {jobs.length === 0 ? (
          <div className="p-6 text-sm text-center text-slate-400">
            {t('dispatch.noJobs', 'No jobs in the queue.')}
          </div>
        ) : (
          jobs.map((job) => (
            <div
              key={job.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', job.id)
                onDragStart?.(job.id)
              }}
              onDragEnd={() => onDragEnd?.()}
              className="px-4 py-3 hover:bg-slate-50 cursor-grab active:cursor-grabbing"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {job.customer_name || job.reference || job.id.slice(0, 8)}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">
                    {job.dropoff_address || job.pickup_address || '—'}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <StatusPill status={job.status} priority={job.priority} />
                    {job.assigned_driver_id && (
                      <span className="text-xs text-slate-500">
                        → {userById.get(job.assigned_driver_id)?.name ?? job.assigned_driver_id.slice(0, 8)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0">{renderTrailing?.(job)}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

function StatusPill({ status, priority }: { status: string; priority?: string }) {
  const priColor =
    priority === 'urgent'
      ? 'bg-red-100 text-red-700'
      : priority === 'high'
        ? 'bg-amber-100 text-amber-700'
        : null
  const statusColor =
    status === 'completed'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'cancelled'
        ? 'bg-slate-100 text-slate-500'
        : status === 'en_route'
          ? 'bg-sky-100 text-sky-700'
          : status === 'assigned'
            ? 'bg-violet-100 text-violet-700'
            : 'bg-slate-100 text-slate-600'
  return (
    <>
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statusColor}`}>{status}</span>
      {priColor && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${priColor}`}>{priority}</span>
      )}
    </>
  )
}
